/**
 * ModerationCaseCardHeader Component
 *
 * Header for moderation case cards with metadata display.
 * Delegates to ContentCard.Header and ContentCard.HeaderMeta from the
 * design system for layout, checkbox, and pipe-separated metadata rendering.
 */

import { ContentCard, BadgeVariant, Badge, BlurToggle, IconButton, IconButtonSize, IconSize, MoreVertical, ButtonType } from '@soteria/design-system-ve';
import type { MouseEvent } from 'react';

import * as styles from './ModerationCaseCard.css';

/** Resolves a nullable display value, falling back to 'N/A' */
function displayOrNA(value?: string | null): string {
  return value || 'N/A';
}

export interface ModerationCaseCardHeaderProps {
  /**
   * Segment name (optional)
   */
  segmentName?: string | null;

  /**
   * Username (optional)
   */
  username?: string | null;

  /**
   * User/Subject ID (optional)
   */
  userId?: string | null;

  /**
   * Formatted timestamp string for display (optional)
   */
  timestamp?: string | null;

  /**
   * Full timestamp string for tooltip display (optional)
   */
  fullTimestamp?: string | null;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Whether to show the action menu trigger button ("...")
   * @default false
   */
  showActionMenu?: boolean;

  /**
   * ID for the action menu trigger button (links to Menu via triggerId for accessibility)
   */
  actionMenuTriggerId?: string;

  /**
   * Handler when action menu button is clicked
   */
  onActionMenuToggle?: () => void;

  /**
   * Whether to display a visual checkbox indicating focused/selected state.
   * @default false
   */
  showCheckbox?: boolean;

  /**
   * Whether the checkbox is checked (card is selected/focused).
   * Only relevant when showCheckbox is true.
   */
  isChecked?: boolean;

  /**
   * Whether to show the per-card blur toggle
   */
  showBlurToggle?: boolean;

  /**
   * Current blur state for the card
   */
  isBlurred?: boolean;

  /**
   * Handler when blur toggle is clicked
   */
  onBlurToggle?: (e: MouseEvent) => void;
}

/** Renders blur toggle and action menu, right-aligned in the header */
function HeaderActions({
  showBlurToggle,
  isBlurred,
  onBlurToggle,
  showActionMenu,
  actionMenuTriggerId,
  onActionMenuToggle,
}: Pick<ModerationCaseCardHeaderProps, 'showBlurToggle' | 'isBlurred' | 'onBlurToggle' | 'showActionMenu' | 'actionMenuTriggerId' | 'onActionMenuToggle'>) {
  const hasBlurToggle = showBlurToggle && onBlurToggle;
  const hasActionMenu = showActionMenu && onActionMenuToggle;

  if (!hasBlurToggle && !hasActionMenu) return null;

  return (
    <div className={styles.headerActions}>
      {hasBlurToggle && <BlurToggle isBlurred={!!isBlurred} onToggle={onBlurToggle} />}
      {hasActionMenu && (
        <IconButton
          aria-label="More actions"
          buttonSize={IconButtonSize.Small}
          buttonType={ButtonType.Secondary}
          icon={MoreVertical}
          iconSize={IconSize.Small}
          id={actionMenuTriggerId}
          title="More actions"
          onClick={e => {
            e.stopPropagation();
            onActionMenuToggle();
          }}
        />
      )}
    </div>
  );
}

/**
 * Header component for ModerationCaseCard
 *
 * Renders metadata in "Subject ID | Username | Timestamp" format with pipe
 * separators and an optional segment badge.
 *
 * @example
 * ```tsx
 * <ModerationCaseCardHeader
 *   segmentName="Screenshots"
 *   username="user123"
 *   userId="abc12345"
 *   timestamp="Jan 21, 2026"
 *   fullTimestamp="Jan 21, 2026, 3:45:12 PM"
 *   showActionMenu
 *   actionMenuTriggerId="moderation-action-menu-trigger"
 *   onActionMenuToggle={() => setIsActionMenuOpen(prev => !prev)}
 * />
 * ```
 */
export function ModerationCaseCardHeader({
  segmentName,
  username,
  userId,
  timestamp,
  fullTimestamp,
  className,
  showActionMenu = false,
  actionMenuTriggerId,
  onActionMenuToggle,
  showCheckbox = false,
  isChecked = false,
  showBlurToggle,
  isBlurred,
  onBlurToggle,
}: ModerationCaseCardHeaderProps) {
  const metadataItems = [
    { label: 'Subject ID', value: displayOrNA(userId) },
    { label: 'User', value: displayOrNA(username) },
    { label: 'Time', value: displayOrNA(timestamp) },
  ];

  return (
    <ContentCard.Header
      checkboxAriaLabel={isChecked ? 'Case selected' : 'Case not selected'}
      className={className}
      isChecked={isChecked}
      showCheckbox={showCheckbox}
    >
      {segmentName && <Badge variant={BadgeVariant.Default}>{segmentName}</Badge>}
      <ContentCard.HeaderMeta items={metadataItems} title={fullTimestamp ?? undefined} />

      <HeaderActions
        actionMenuTriggerId={actionMenuTriggerId}
        isBlurred={isBlurred}
        showActionMenu={showActionMenu}
        showBlurToggle={showBlurToggle}
        onActionMenuToggle={onActionMenuToggle}
        onBlurToggle={onBlurToggle}
      />
    </ContentCard.Header>
  );
}
